import { useState, useEffect } from "react";
import axios from "axios";
import botImage1 from "../../assets/images/chatbot_icon.png";
import botImage2 from '../../assets/videos/chatbot_reverse_animate.webm';
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const VerificationScreen = ({
  onClose,
  setScreen,
  setUser,
}) => {

  const [form, setForm] = useState({
    name: "",
    email: "",
    mobile: "", 
  });

  const [otpData, setOtpData] = useState({
    emailOtp: Array(6).fill(""),
  });

  const [emailOtpSent, setEmailOtpSent] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [emailTimer, setEmailTimer] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");

  // Sync existing local storage session on mount if available
// Sync existing local session and fetch name if missing from storage
  useEffect(() => {
    const savedEmail = localStorage.getItem("email");
    const isLogged = localStorage.getItem("loggedIn");
    let savedName = localStorage.getItem("name");
    const savedPhone = localStorage.getItem("phone");

    if (isLogged === "true" && savedEmail) {
      if (!savedName || savedName === "User") {
        // Fetch real name from backend if it wasn't saved in localStorage yet
        axios.get(`${API_BASE_URL}/chatbot/${encodeURIComponent(savedEmail)}`)
          .then((res) => {
            const fetchedName = res.data?.user?.name || res.data?.name || "User";
            localStorage.setItem("name", fetchedName);
            setUser({ name: fetchedName, email: savedEmail, mobile: savedPhone || "" });
            setScreen("welcome");
          })
          .catch(() => {
            setUser({ name: "User", email: savedEmail, mobile: savedPhone || "" });
            setScreen("welcome");
          });
      } else {
        setUser({ name: savedName, email: savedEmail, mobile: savedPhone || "" });
        setScreen("welcome");
      }
    }
  }, [setUser, setScreen]);

  // Email Countdown Logic
  useEffect(() => {
    let interval;
    if (emailTimer > 0) {
      interval = setInterval(() => {
        setEmailTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [emailTimer]);

  // 1. POST /register call (Triggers email OTP send or bypasses if already registered)
  const handleSendEmailOtp = async () => {
    if (!form.email || !form.name || !form.mobile) {
      setErrorMessage("Please fill all fields.");
      return;
    }

    setErrorMessage("");

    // First: check if the user already exists in the database
    try {
      const accessResp = await axios.get(`${API_BASE_URL}/chatbot/${encodeURIComponent(form.email)}`);
      if (accessResp.status === 200) {
        const resolvedName = accessResp.data.user?.name || accessResp.data.name || form.name;
        const resolvedPhone = accessResp.data.user?.phone || accessResp.data.phone || form.mobile;
        
        // User exists! Skip OTP and mark as verified immediately
        setEmailVerified(true);
        setForm(prev => ({ ...prev, name: resolvedName, mobile: resolvedPhone }));

        // Sync to standard localStorage keys for cross-app compatibility
        localStorage.setItem("loggedIn", "true");
        localStorage.setItem("email", form.email);
        localStorage.setItem("name", resolvedName);
        localStorage.setItem("phone", resolvedPhone);
        return;
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status !== 403 && status !== 404 && status !== 409) {
        if (err.response) {
          setErrorMessage(err.response.data?.detail || `Server error: ${err.response.status}`);
        } else if (err.request) {
          setErrorMessage("Cannot connect to server.");
        } else {
          setErrorMessage("An unexpected error occurred. Please try again.");
        }
        return;
      }
    }

    // If user does not exist, proceed to register and send OTP
    try {
      const response = await axios.post(`${API_BASE_URL}/register`, {
        name: form.name,
        email: form.email,
        phone: form.mobile,
      });

      if (response.status === 200 || response.status === 201) {
        setEmailOtpSent(true);
        setEmailTimer(30);
      }
    } catch (error) {
      console.error("Error registering user / sending OTP:", error);
      if (error.response) {
        const errorData = error.response.data;
        if (errorData && errorData.detail) {
          if (Array.isArray(errorData.detail)) {
            setErrorMessage(errorData.detail.map(err => err.msg).join(", "));
          } else {
            setErrorMessage(errorData.detail);
          }
        } else {
          setErrorMessage(`Server responded with status code ${error.response.status}`);
        }
      } else {
        setErrorMessage("An unexpected error occurred. Please try again.");
      }
    }
  };

  // 2. POST /verify-otp call (Verifies 6 digit code)
  const handleVerifyEmailOtp = async () => {
    const otp = otpData.emailOtp.join("");
    if (otp.length !== 6) return;

    setErrorMessage("");
    try {
      const response = await axios.post(`${API_BASE_URL}/verify-otp`, {
        email: form.email,
        otp: otp
      });

      if (response.status === 200) {
        setEmailVerified(true);
        setOtpData({ emailOtp: Array(6).fill("") });

        // Save session values to localStorage for cross-platform sharing
        localStorage.setItem("loggedIn", "true");
        localStorage.setItem("email", form.email);
        localStorage.setItem("name", form.name);
        localStorage.setItem("phone", form.mobile);
      }
    } catch (error) {
      console.error("OTP verification failed:", error);
      setErrorMessage(error.response?.data?.detail || "Invalid OTP. Please try again.");
    }
  };

  const handleResendEmailOtp = () => {
    setOtpData({ emailOtp: Array(6).fill("") });
    handleSendEmailOtp();
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value) || value.length > 1) return;

    setOtpData(prev => {
      const newOtp = [...prev.emailOtp];
      newOtp[index] = value.slice(-1);
      return { emailOtp: newOtp };
    });

    if (value && index < 5) {
      const nextInput = document.getElementById(`emailOtp-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otpData.emailOtp[index] && index > 0) {
      const prevInput = document.getElementById(`emailOtp-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  const handleStart = () => {
    localStorage.setItem("loggedIn", "true");
    localStorage.setItem("email", form.email);
    localStorage.setItem("name", form.name);
    localStorage.setItem("phone", form.mobile);

    setUser(form);
    setScreen("welcome");
  };

  const isStep1Valid = form.name && form.email && form.mobile;

  return (
    <>
      <div className="chat-header">
        <div className="chat-header-left">
          <img src={botImage1} alt="" className="chat-avatar" />
          <div>
            <h4 className="chat-title">IntelliBot</h4>
            <div className="chat-status">Online</div>
          </div>
        </div>
        <button className="chat-close" onClick={onClose}>×</button>
      </div>

      <div className="chat-body verification-body">
        <div className="welcome-section-large">
          <video
            src={botImage2}
            className="welcome-avatar-large"
            autoPlay
            muted
            playsInline
            loop
          />
          <div className="welcome-text-wrapper">
            <h2 className="welcome-title-large">
              Welcome to<br />
              <span className="brand-highlight">IntelliBot</span>
            </h2>
          </div>
        </div>
        <p className="welcome-subtitle-large">Please verify your details to continue.</p>

        {errorMessage && (
          <div className="error-message-banner" style={{ color: '#ff4d4f', marginBottom: '12px', fontSize: '14px', fontWeight: '500' }}>
            ⚠️ {errorMessage}
          </div>
        )}

        <div className="form-fields-container">
          <div className="form-field">
            <label className="field-label">Full Name <span className="required">*</span></label>
            <input
              className="form-input"
              placeholder="John Doe"
              value={form.name}
              disabled={emailOtpSent || emailVerified}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </div>

          <div className="form-field">
            <div className="field-header">
              <label className="field-label">Email Address <span className="required">*</span></label>
              {emailVerified && <span className="verified-badge">✓ Verified</span>}
            </div>
            <div className="input-with-button">
              <input
                className="form-input"
                placeholder="name@example.com"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                disabled={emailOtpSent || emailVerified}
              />
              {!emailVerified && (
                <button className="action-button" onClick={handleSendEmailOtp} disabled={!isStep1Valid || emailTimer > 0}>
                  {emailTimer > 0 ? `Resend (${emailTimer}s)` : "Check & Verify"}
                </button>
              )}
            </div>

            {emailOtpSent && !emailVerified && (
              <div className="otp-section">
                <div className="otp-inputs-container">
                  {[0, 1, 2, 3, 4, 5].map((index) => (
                    <input
                      key={index}
                      id={`emailOtp-${index}`}
                      type="text"
                      inputMode="numeric"
                      maxLength="1"
                      className="otp-single-input"
                      value={otpData.emailOtp[index]}
                      onChange={(e) => handleOtpChange(index, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(index, e)}
                    />
                  ))}
                </div>
                <div className="otp-actions">
                  <button
                    className="verify-button"
                    onClick={handleVerifyEmailOtp}
                    disabled={otpData.emailOtp.join("").length !== 6}
                  >
                    Verify
                  </button>
                </div>

                <button
                  className="resend-link"
                  onClick={handleResendEmailOtp}
                  disabled={emailTimer > 0}
                  style={{ opacity: emailTimer > 0 ? 0.5 : 1, cursor: emailTimer > 0 ? 'not-allowed' : 'pointer' }}
                >
                  {emailTimer > 0 ? `Resend OTP in ${emailTimer}s` : "Didn't receive the code? Resend OTP"}
                </button>
              </div>
            )}
          </div>

          <div className="form-field">
            <label className="field-label">
              Mobile Number <span className="required">*</span>
            </label>

            <PhoneInput
              country="in"
              enableSearch={true}
              disableSearch={false}
              autoFormat={true}
              countryCodeEditable={false}
              value={form.mobile}
              disabled={emailOtpSent || emailVerified}
              onChange={(value) => {
                const formattedValue = value.startsWith("+") ? value : "+" + value;
                setForm((prev) => ({ ...prev, mobile: formattedValue }));
              }}
              prefix="+"
              containerClass="phone-container"
              inputClass="phone-input-field"
              buttonClass="phone-country-button"
              dropdownClass="phone-country-dropdown"
              searchClass="hidden-phone-search"
              inputProps={{
                name: "mobile",
                autoComplete: "tel",
                required: true,
              }}
            />
          </div>
        </div>

        <button
          className="start-button"
          onClick={handleStart}
          disabled={!isStep1Valid || !emailVerified}
        >
          Start →
        </button>
      </div>
    </>
  );
};

export default VerificationScreen;